const get = require('lodash/get')

// /* ChangesFeed
//  *
//  * Usage example, with a checkpoint object supplied by the caller:
//  * ```
//  * const since = await checkpoint.read()
//  *
//  * const feed = new ChangesFeed(db, { since })
//  * const changes = await feed.readAll()
//  * const docs = changes.map(c => c.doc)
//  *
//  * await checkpoint.write(feed.lastSeq)
//  * ```
//  */
class ChangesFeed {
  constructor (db, options) {
    this.db = db
    // batchSize: Amount of changes to fetch at once
    this.batchSize = get(options, 'batchSize', 100)
    // maxChanges: Maximum number of changes to read. At least equal to batch size.
    this.maxChanges = get(options, 'maxChanges', Math.max(this.batchSize, 100))
    // includeDocs: Whether to include the documents when reading
    this.includeDocs = get(options, 'includeDocs', true)
    // includeDeleted: Whether to include changes for deleted docs
    this.includeDeleted = get(options, 'includeDeleted', false)
    // since: Sequence id after which to start reading
    this.since = get(options, 'since', 0)
    // readUntilEnd: Fetch changes until no more changes are found
    this.readUntilEnd = get(options, 'readUntilEnd', false)
    if (this.readUntilEnd) {
      this.maxChanges = 0
    }
    // descending: Read changes in descending order
    // Warning: Descending will only work for a single batch.
    this.descending = get(options, 'descending')
    if (this.descending) {
      this.maxChanges = this.batchSize
      this.readUntilEnd = false
    }

    this.lastSeq = this.since
    this.numChangesRead = 0
    this.done = false
  }

  async readBatch () {
    let batchSize = this.readUntilEnd
      ? this.batchSize
      : Math.min(this.batchSize, this.maxChanges - this.numChangesRead)
    const changes = await this.db.changes({
      since: this.lastSeq,
      limit: batchSize,
      include_docs: this.includeDocs,
      descending: this.descending
    })
    if (changes.results.length === 0) {
      this.done = true
      return []
    }
    this.numChangesRead += changes.results.length
    this.lastSeq = changes.results[changes.results.length - 1].seq

    const filteredChanges = []
    for (const result of changes.results) {
      if (!this.includeDeleted && result.deleted) {
        continue
      }
      filteredChanges.push(result)
    }
    this.done = (changes.results.length < batchSize) ||
      (!this.readUntilEnd && this.numChangesRead >= this.maxChanges)
    return filteredChanges
  }

  async readAll () {
    this.done = false
    const allChanges = []
    while (!this.done) {
      const changesBatch = await this.readBatch()
      allChanges.push(...changesBatch)
    }
    return allChanges
  }
}

/* Read changes and apply handler with changes.
 * The checkpoint will only be set if we see no error.
 * The way this works is:
 * 1. Read n checkpoints
 * 2. Read n changes feeds
 * 3. Apply handler with n lists of changes
 * 4. Write n checkpoints
 * If any step before 4 fails this will not write checkpoints.
 *
 * This can read from multiple dbs. Every config needs a checkpoint object with
 * async read() and write(seq) methods; server callers create it with
 * backend's createOwnedCheckpoint.
 * Usage:
 * ```
 * const changesConfigs = [
 * {db: PouchDB, checkpointId: string, checkpoint: {read, write}, feedOptions: ChangesFeed.options}
 * ...
 * {db, checkpointId, checkpoint, feedOptions}
 * ]
 * const handler = async function ([...changes], ..., [...changes]) { ... }
 * await withChanges(changesConfigs, handler)
 * ```
  */
async function withChanges (changesConfigs = [], handler) {
  if (changesConfigs.length === 0) {
    return
  }
  if (!handler) {
    throw new Error('handler is required')
  }
  // validate every config before reading any checkpoint or feed
  const checkpoints = []
  for (const c of changesConfigs) {
    if (!c.db) {
      throw new Error('options.db is required')
    }
    if (!c.checkpointId) {
      throw new Error('options.checkpointId is required')
    }
    const checkpoint = c.checkpoint
    if (!checkpoint || typeof checkpoint.read !== 'function' || typeof checkpoint.write !== 'function') {
      throw new Error(`options.checkpoint with read and write methods is required for ${c.checkpointId}`)
    }
    checkpoints.push(checkpoint)
  }

  // read checkpoints
  const startSeqs = await Promise.all(checkpoints.map(c => {
    return (async function () {
      try {
        return await c.read()
      } catch (e) {
        console.log('error reading checkpoint with id', c.id)
        throw e
      }
    }())
  }))

  // construct feeds
  const feeds = []
  for (let i = 0; i < changesConfigs.length; i++) {
    const config = changesConfigs[i]
    const lastSeq = startSeqs[i]
    const feed = new ChangesFeed(config.db, Object.assign({since: lastSeq}, config.feedOptions))
    feeds.push(feed)
  }

  // read all the changes feeds
  const changesPerFeed = await Promise.all(feeds.map(f => {
    try {
      return f.readAll()
    } catch (error) {
      console.log('Error reading changes feed', f.db.name)
      throw error
    }
  }))

  const stats = feeds.map(({ db, since, lastSeq }) => ({
    dbName: db.name,
    since,
    lastSeq
  }))

  // call the handler with the changes
  try {
    await handler(changesPerFeed, stats)
  } catch (e) {
    console.log('error applying changes handler')
    throw e
  }

  // write the checkpoints and generate some stats to return
  const checkpointPromises = []
  for (let i = 0; i < changesConfigs.length; ++i) {
    const lastSeq = feeds[i].lastSeq
    const config = changesConfigs[i]
    checkpointPromises.push(async function () {
      try {
        return checkpoints[i].write(lastSeq)
      } catch (e) {
        console.log('error setting checkpoing for db', config.db.name)
        throw e
      }
    }())
  }
  try {
    await Promise.all(checkpointPromises)
  } catch (e) {
    console.log('error setting checkpoints. this is a grave error')
    throw e
  }

  return stats
}

module.exports = {
  ChangesFeed,
  withChanges
}
